import React, { useEffect, useMemo, useState } from 'react';
import { Lightbulb, Plus, Search, LayoutGrid, List, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { mccGet, mccPost, mccPatch, mccDelete } from './mccApi';
import { SectionHeader, EmptyState, StatusPill, PlatformIcon, Avatar, PriorityPill } from './mccUi';
import { IDEA_STAGES, PLATFORMS, statusTone, humanDate } from './mccConstants';
import MccKanban from './MccKanban';
import MccCreateDialog from './MccCreateDialog';
import MccStatusGroup from './MccStatusGroup';

export default function MccIdeas({ team, onProjectCreated }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('kanban');
  const [filters, setFilters] = useState({ status: '', platform: '', q: '' });
  const [openDialog, setOpenDialog] = useState(false);

  const nameOf = (uid) => (team.find((t) => t.id === uid) || {}).name || uid;

  const load = async () => {
    setLoading(true);
    try {
      const res = await mccGet('/marketing/ideas', {
        status: filters.status, platform: filters.platform, q: filters.q,
      });
      setItems(res.items || []);
    } catch (e) { toast.error(e.message || 'Failed to load ideas'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.status, filters.platform]);

  const filtered = useMemo(() => {
    if (!filters.q) return items;
    const ql = filters.q.toLowerCase();
    return items.filter((i) =>
      (i.title || '').toLowerCase().includes(ql) ||
      (i.description || '').toLowerCase().includes(ql) ||
      (i.hook || '').toLowerCase().includes(ql)
    );
  }, [items, filters.q]);

  const convert = async (idea) => {
    try {
      const res = await mccPost(`/marketing/ideas/${idea.id}/convert-to-project`);
      toast.success('Converted to content project');
      onProjectCreated?.(res.id);
      await load();
    } catch (e) { toast.error(e.message || 'Convert failed'); }
  };

  const advance = async (idea, status) => {
    try { await mccPatch(`/marketing/ideas/${idea.id}`, { status }); toast.success(`Moved to ${statusTone(status).label}`); await load(); }
    catch (e) { toast.error(e.message || 'Update failed'); }
  };

  const del = async (idea) => {
    if (!window.confirm(`Delete idea "${idea.title}"?`)) return;
    try { await mccDelete(`/marketing/ideas/${idea.id}`); await load(); toast.success('Deleted'); }
    catch (e) { toast.error(e.message || 'Delete failed'); }
  };

  return (
    <div className="p-6 space-y-4">
      <SectionHeader
        title="Ideas Board"
        subtitle="Every content idea in one place. Approve, assign, then convert into a full production project."
        icon={Lightbulb}
        right={<Button onClick={() => setOpenDialog(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="mcc-idea-create">
          <Plus className="w-4 h-4 mr-1" /> New Idea
        </Button>}
      />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400 pointer-events-none" />
          <Input className="pl-8 w-64" placeholder="Search ideas…" value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} />
        </div>
        <Select value={filters.status || '__all__'} onValueChange={(v) => setFilters((f) => ({ ...f, status: v === '__all__' ? '' : v }))}>
          <SelectTrigger className="h-9 w-44"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All statuses</SelectItem>
            {IDEA_STAGES.map((s) => <SelectItem key={s} value={s}>{statusTone(s).label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.platform || '__all__'} onValueChange={(v) => setFilters((f) => ({ ...f, platform: v === '__all__' ? '' : v }))}>
          <SelectTrigger className="h-9 w-40"><SelectValue placeholder="Platform" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All platforms</SelectItem>
            {PLATFORMS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto flex rounded-md border border-slate-200 overflow-hidden">
          <button onClick={() => setView('kanban')} className={`px-2.5 py-1.5 ${view === 'kanban' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-100 hover:text-slate-900'}`}><LayoutGrid className="w-3.5 h-3.5" /></button>
          <button onClick={() => setView('list')} className={`px-2.5 py-1.5 ${view === 'list' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-100 hover:text-slate-900'}`}><List className="w-3.5 h-3.5" /></button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500 p-8 text-center">Loading…</p>
      ) : filtered.length === 0 ? (
        <EmptyState icon={Lightbulb} title="No ideas yet" body="Add your first content idea to get the pipeline flowing."
          cta={<Button onClick={() => setOpenDialog(true)}>Add first idea</Button>} />
      ) : view === 'kanban' ? (
        <MccKanban stages={IDEA_STAGES} items={filtered} variant="idea" nameOf={nameOf}
          onOpen={(idea) => openIdea(idea, advance, convert, del)}
          onMove={(item, newStatus) => advance(item, newStatus)} />
      ) : (
        <div className="space-y-3">
          {IDEA_STAGES.map((stage) => {
            const rows = filtered.filter((i) => i.status === stage);
            if (rows.length === 0) return null;
            return (
              <MccStatusGroup key={stage} status={stage} count={rows.length}>
                <table className="w-full text-sm">
                  <thead className="bg-slate-50/60 text-[10px] uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Title / Hook</th>
                      <th className="px-3 py-2 text-left">Person</th>
                      <th className="px-3 py-2 text-left">Platform</th>
                      <th className="px-3 py-2 text-left">Assigned</th>
                      <th className="px-3 py-2 text-left">Due</th>
                      <th className="px-3 py-2 text-left">Priority</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {rows.map((i) => (
                      <tr key={i.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => openIdea(i, advance, convert, del)}>
                        <td className="px-3 py-2">
                          <p className="font-semibold text-slate-900">{i.title}</p>
                          {i.hook ? <p className="text-[11px] text-slate-500 italic mt-0.5">&ldquo;{i.hook}&rdquo;</p> : null}
                        </td>
                        <td className="px-3 py-2 text-slate-700">{i.person_in_content || '—'}</td>
                        <td className="px-3 py-2">{i.primary_platform ? <PlatformIcon platform={i.primary_platform} size={18} /> : '—'}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1">
                            {(i.assigned_to || []).slice(0, 3).map((uid) => <Avatar key={uid} name={nameOf(uid)} size={18} />)}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-[12px] text-slate-600">{humanDate(i.requested_completion_date)}</td>
                        <td className="px-3 py-2"><PriorityPill priority={i.priority} /></td>
                        <td className="px-3 py-2 text-right">
                          {i.status === 'approved' && (
                            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); convert(i); }}
                              data-testid={`mcc-idea-convert-${i.id}`}>
                              Convert <ArrowRight className="w-3 h-3 ml-1" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </MccStatusGroup>
            );
          })}
        </div>
      )}

      <MccCreateDialog open={openDialog} onClose={() => setOpenDialog(false)} kind="idea" team={team}
        onCreated={load} />
    </div>
  );
}

// Simple confirm-style modal via prompt() — a lightweight approach for the row
// actions. For real work, users open the kanban card and use the drawer.
function openIdea(idea, advance, convert, del) {
  const choice = window.prompt(
    `Idea: ${idea.title}\n\nType:\n- APPROVE to move to approved\n- REVIEW to move to under_review\n- REJECT to delete\n- CONVERT to create a content project\n- (Enter) to cancel`,
    ''
  );
  if (!choice) return;
  const c = choice.trim().toUpperCase();
  if (c === 'APPROVE') advance(idea, 'approved');
  else if (c === 'REVIEW') advance(idea, 'under_review');
  else if (c === 'REJECT') del(idea);
  else if (c === 'CONVERT') convert(idea);
}
